import { Link } from "wouter";
import { cn } from "@/lib/utils";

export type HeatmapCell = {
  date: string;
  count: number;
  href?: string;
  tooltip: string;
};

type Props = {
  cells: HeatmapCell[];
  emptyLabel?: string;
};

const MS_PER_DAY = 86_400_000;
const WEEKDAY_LABELS = ["Mon", "Wed", "Fri"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function utcDayKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function intensityClass(count: number, max: number): string {
  if (count <= 0 || max <= 0) return "bg-muted/40";
  const ratio = count / max;
  if (ratio > 0.75) return "bg-primary";
  if (ratio > 0.5) return "bg-primary/75";
  if (ratio > 0.25) return "bg-primary/55";
  return "bg-primary/35";
}

export function AttendanceHeatmap({ cells, emptyLabel = "No attendance in the last 12 months." }: Props) {
  const byDate = new Map<string, HeatmapCell>();
  let max = 0;
  for (const c of cells) {
    byDate.set(c.date, c);
    if (c.count > max) max = c.count;
  }

  const today = new Date();
  const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const endSaturday = new Date(todayUtc);
  endSaturday.setUTCDate(endSaturday.getUTCDate() + (6 - endSaturday.getUTCDay()));
  const weeks = 53;
  const totalDays = weeks * 7;
  const startSunday = new Date(endSaturday.getTime() - (totalDays - 1) * MS_PER_DAY);

  const columns: Array<Array<{ date: Date; cell: HeatmapCell | undefined; inFuture: boolean }>> = [];
  const monthHeaders: Array<{ col: number; label: string }> = [];
  let lastMonth = -1;
  for (let w = 0; w < weeks; w++) {
    const col: Array<{ date: Date; cell: HeatmapCell | undefined; inFuture: boolean }> = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(startSunday.getTime() + (w * 7 + d) * MS_PER_DAY);
      const inFuture = date.getTime() > todayUtc.getTime();
      col.push({ date, cell: byDate.get(utcDayKey(date)), inFuture });
    }
    const firstOfWeek = col[0]!.date;
    const m = firstOfWeek.getUTCMonth();
    if (m !== lastMonth && firstOfWeek.getUTCDate() <= 7) {
      monthHeaders.push({ col: w, label: MONTH_LABELS[m]! });
      lastMonth = m;
    }
    columns.push(col);
  }

  if (cells.length === 0) {
    return <div className="text-sm text-muted-foreground py-8 text-center" data-testid="attendance-empty">{emptyLabel}</div>;
  }

  return (
    <div className="overflow-x-auto" data-testid="attendance-heatmap">
      <div className="inline-block min-w-full">
        <div className="flex">
          <div className="flex flex-col gap-[3px] mr-2 text-[10px] text-muted-foreground pt-4">
            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
              <div key={d} className="h-3 leading-3">
                {d === 1 ? WEEKDAY_LABELS[0] : d === 3 ? WEEKDAY_LABELS[1] : d === 5 ? WEEKDAY_LABELS[2] : ""}
              </div>
            ))}
          </div>
          <div>
            <div className="relative h-4 mb-1">
              {monthHeaders.map((h) => (
                <span
                  key={`${h.col}-${h.label}`}
                  className="absolute text-[10px] text-muted-foreground"
                  style={{ left: `${h.col * 15}px` }}
                >
                  {h.label}
                </span>
              ))}
            </div>
            <div className="flex gap-[3px]">
              {columns.map((col, ci) => (
                <div key={ci} className="flex flex-col gap-[3px]">
                  {col.map((cell, ri) => {
                    const key = `${ci}-${ri}`;
                    if (cell.inFuture) {
                      return <div key={key} className="w-3 h-3 rounded-sm bg-transparent" />;
                    }
                    const count = cell.cell?.count ?? 0;
                    const cls = cn("w-3 h-3 rounded-sm transition-colors", intensityClass(count, max));
                    const tooltip = cell.cell?.tooltip ?? `${utcDayKey(cell.date)}: no attendance`;
                    if (cell.cell?.href) {
                      return (
                        <Link
                          key={key}
                          href={cell.cell.href}
                          className={cn(cls, "hover:ring-2 hover:ring-primary/60 cursor-pointer")}
                          title={tooltip}
                          aria-label={tooltip}
                        />
                      );
                    }
                    return <div key={key} className={cls} title={tooltip} aria-label={tooltip} />;
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
          <span>Less</span>
          <div className="w-3 h-3 rounded-sm bg-muted/40" />
          <div className="w-3 h-3 rounded-sm bg-primary/35" />
          <div className="w-3 h-3 rounded-sm bg-primary/55" />
          <div className="w-3 h-3 rounded-sm bg-primary/75" />
          <div className="w-3 h-3 rounded-sm bg-primary" />
          <span>More</span>
        </div>
      </div>
    </div>
  );
}
